import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';

import type { TippingPaymentOption } from './types';

const paymentOptions: Array<{
  description: string;
  icon: React.ElementType;
  id: TippingPaymentOption;
  label: string;
}> = [
  {
    description: 'Card, Apple Pay, or Google Pay through Stripe',
    icon: CreditCardRoundedIcon,
    id: 'card',
    label: 'Card or wallet',
  },
  {
    description: 'Pay through Cash App',
    icon: AttachMoneyRoundedIcon,
    id: 'cash_app',
    label: 'Cash App Pay',
  },
];

interface TippingPaymentOptionsProps {
  onChange: (paymentOption: TippingPaymentOption) => void;
  paymentOption: TippingPaymentOption;
}

const TippingPaymentOptions: React.FC<TippingPaymentOptionsProps> = ({
  onChange,
  paymentOption,
}) => (
  <Stack spacing={1}>
    <Typography variant="subtitle2">Payment Method</Typography>
    {paymentOptions.map((option) => {
      const selected = paymentOption === option.id;
      const OptionIcon = option.icon;

      return (
        <Button
          color="inherit"
          key={option.id}
          onClick={() => onChange(option.id)}
          sx={{
            bgcolor: selected ? 'common.white' : 'grey.200',
            border: '1px solid',
            borderColor: selected
              ? 'text.primary'
              : 'rgba(145, 158, 171, 0.20)',
            color: 'var(--cx-brand-button-text)',
            borderRadius: 1.5,
            justifyContent: 'flex-start',
            minHeight: 64,
            p: 1.25,
            '&:hover': {
              bgcolor: selected
                ? 'common.white'
                : 'var(--cx-brand-primary-soft)',
              borderColor: selected
                ? 'text.primary'
                : 'rgba(145, 158, 171, 0.20)',
            },
          }}
          type="button"
          variant="outlined"
        >
          <Box
            sx={{
              alignItems: 'center',
              bgcolor:
                option.id === 'cash_app'
                  ? '#00D64F'
                  : 'var(--cx-brand-button-hover-bg)',
              borderRadius: 1,
              color:
                option.id === 'cash_app'
                  ? '#FFFFFF'
                  : 'var(--cx-brand-button-text)',
              display: 'flex',
              flexShrink: 0,
              fontSize: 22,
              height: 38,
              justifyContent: 'center',
              mr: 1.5,
              width: 38,
            }}
          >
            <OptionIcon sx={{ fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography variant="subtitle2">{option.label}</Typography>
            <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
              {option.description}
            </Typography>
          </Box>
          <Radio
            checked={selected}
            sx={{
              color: 'var(--cx-brand-text-muted)',
              p: 0.5,
              '&.Mui-checked': { color: 'text.primary' },
            }}
          />
        </Button>
      );
    })}
  </Stack>
);

export default TippingPaymentOptions;
